import * as THREE from 'three';
import { PointCloudTreeNode } from './point-cloud-tree.js';
import { Utils } from '../shared/utils.js';
import { PointAttributes } from '../types/PointAttributes.js';
import {
  getGlobalConfig,
  getSasToken,
  incrementNodesLoading,
  decrementNodesLoading,
} from '../services/config.service.js';

export class PointCloudOctreeGeometry {
  url: string | null;
  octreeDir: string | null;
  spacing: number;
  boundingBox: THREE.Box3 | null;
  root: PointCloudOctreeGeometryNode | null;
  nodes: Record<string, PointCloudOctreeGeometryNode> | null;
  pointAttributes: PointAttributes | null;
  hierarchyStepSize: number;
  loader: { load(node: PointCloudOctreeGeometryNode): void } | null;
  projection: string | null;
  fallbackProjection: string | null;
  tightBoundingBox: THREE.Box3 | null;
  offset: THREE.Vector3 | null;

  constructor() {
    this.url = null;
    this.octreeDir = null;
    this.spacing = 0;
    this.boundingBox = null;
    this.root = null;
    this.nodes = null;
    this.pointAttributes = null;
    this.hierarchyStepSize = -1;
    this.loader = null;
    this.projection = null;
    this.fallbackProjection = null;
    this.tightBoundingBox = null;
    this.offset = null;
  }
}

interface GeometryNodeBuffer {
  data: ArrayBuffer;
  numElements: number;
  offset: (name: string) => number;
  stride: number;
}

export class PointCloudOctreeGeometryNode extends PointCloudTreeNode {
  static IDCount = 0;

  id: number;
  name: string;
  index: number;
  pcoGeometry: PointCloudOctreeGeometry;
  geometry: THREE.BufferGeometry | null;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  children: Record<number, PointCloudOctreeGeometryNode | undefined>;
  numPoints: number;
  level: number | null;
  loaded: boolean;
  loading: boolean;
  hasChildren: boolean;
  spacing: number;
  oneTimeDisposeHandlers: Array<() => void>;
  parent: PointCloudOctreeGeometryNode | null;
  buffer?: GeometryNodeBuffer;
  density?: number;
  estimatedSpacing?: number;
  mean?: THREE.Vector3;

  constructor(name: string, pcoGeometry: PointCloudOctreeGeometry, boundingBox: THREE.Box3) {
    super();

    this.id = PointCloudOctreeGeometryNode.IDCount++;
    this.name = name;
    this.index = parseInt(name.charAt(name.length - 1));
    this.pcoGeometry = pcoGeometry;
    this.geometry = null;
    this.boundingBox = boundingBox;
    this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
    this.children = {};
    this.numPoints = 0;
    this.level = null;
    this.loaded = false;
    this.loading = false;
    this.hasChildren = false;
    this.spacing = 0;
    this.parent = null;
    this.oneTimeDisposeHandlers = [];
  }

  isGeometryNode(): boolean {
    return true;
  }

  getLevel(): number | null {
    return this.level;
  }

  isTreeNode(): boolean {
    return false;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getBoundingSphere(): THREE.Sphere {
    return this.boundingSphere;
  }

  getBoundingBox(): THREE.Box3 {
    return this.boundingBox;
  }

  getChildren(): PointCloudOctreeGeometryNode[] {
    let children: PointCloudOctreeGeometryNode[] = [];

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]!);
      }
    }

    return children;
  }

  getURL(): string {
    const url = this.pcoGeometry.octreeDir + '/' + this.getHierarchyPath() + '/' + this.name;
    return url;
  }

  getHierarchyPath(): string {
    let path = 'r/';

    let hierarchyStepSize = this.pcoGeometry.hierarchyStepSize;
    let indices = this.name.substr(1);

    let numParts = Math.floor(indices.length / hierarchyStepSize);
    for (let i = 0; i < numParts; i++) {
      path += indices.substr(i * hierarchyStepSize, hierarchyStepSize) + '/';
    }

    path = path.slice(0, -1);

    return path;
  }

  addChild(child: PointCloudOctreeGeometryNode): void {
    this.children[child.index] = child;
    child.parent = this;
  }

  load(): void {
    const config = getGlobalConfig();
    if (
      this.loading === true ||
      this.loaded === true ||
      config.numNodesLoading >= config.maxNodesLoading
    ) {
      return;
    }

    this.loading = true;

    incrementNodesLoading();

    if (
      this.level !== null &&
      this.level % this.pcoGeometry.hierarchyStepSize === 0 &&
      this.hasChildren
    ) {
      this.loadHierachyThenPoints();
    } else {
      this.loadPoints();
    }
  }

  loadPoints(): void {
    this.pcoGeometry.loader?.load(this);
  }

  loadHierachyThenPoints(): void {
    let node: PointCloudOctreeGeometryNode = this;

    interface DecodedNode {
      children: number;
      numPoints: number;
      name: string;
    }

    // load hierarchy
    let callback = function (node: PointCloudOctreeGeometryNode, hbuffer: ArrayBuffer) {
      let tStart = performance.now();

      let view = new DataView(hbuffer);

      let stack: DecodedNode[] = [];
      let children = view.getUint8(0);
      let numPoints = view.getUint32(1, true);
      node.numPoints = numPoints;
      stack.push({ children: children, numPoints: numPoints, name: node.name });

      let decoded: DecodedNode[] = [];

      let offset = 5;
      while (stack.length > 0) {
        let snode = stack.shift()!;
        let mask = 1;
        for (let i = 0; i < 8; i++) {
          if ((snode.children & mask) !== 0) {
            let childName = snode.name + i;

            let childChildren = view.getUint8(offset);
            let childNumPoints = view.getUint32(offset + 1, true);

            stack.push({ children: childChildren, numPoints: childNumPoints, name: childName });

            decoded.push({ children: childChildren, numPoints: childNumPoints, name: childName });

            offset += 5;
          }

          mask = mask * 2;
        }

        if (offset === hbuffer.byteLength) {
          break;
        }
      }

      let nodes: Record<string, PointCloudOctreeGeometryNode> = {};
      nodes[node.name] = node;
      let pco = node.pcoGeometry;

      for (let i = 0; i < decoded.length; i++) {
        let name = decoded[i].name;
        let decodedNumPoints = decoded[i].numPoints;
        let index = parseInt(name.charAt(name.length - 1));
        let parentName = name.substring(0, name.length - 1);
        let parentNode = nodes[parentName];
        let level = name.length - 1;
        let boundingBox = Utils.createChildAABB(parentNode.boundingBox, index);

        let currentNode = new PointCloudOctreeGeometryNode(name, pco, boundingBox);
        currentNode.level = level;
        currentNode.numPoints = decodedNumPoints;
        currentNode.hasChildren = decoded[i].children > 0;
        currentNode.spacing = pco.spacing / Math.pow(2, level);
        parentNode.addChild(currentNode);
        nodes[name] = currentNode;
      }

      let duration = performance.now() - tStart;
      if (duration > 5) {
        let msg = `duration: ${duration}ms, numNodes: ${decoded.length}`;
        console.log(msg);
      }

      node.loadPoints();
    };
    if (node.level !== null && node.level % node.pcoGeometry.hierarchyStepSize === 0) {
      let hurl =
        node.pcoGeometry.octreeDir + '/' + node.getHierarchyPath() + '/' + node.name + '.hrc';

      const sasToken = getSasToken();
      if (sasToken) {
        const separator = hurl.includes('?') ? '&' : '?';
        hurl = `${hurl}${separator}${sasToken}`;
      }

      fetch(hurl)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else {
            console.error(
              'Failed to load file! HTTP status: ' + response.status + ', file: ' + hurl,
            );
            decrementNodesLoading();
            return null;
          }
        })
        .then(hbuffer => {
          if (hbuffer) {
            callback(node, hbuffer);
          }
        })
        .catch(e => {
          console.error('Error while loading point cloud: ' + e);
          decrementNodesLoading();
        });
    }
  }

  getNumPoints(): number {
    return this.numPoints;
  }

  dispose(): void {
    if (this.geometry && this.parent != null) {
      this.geometry.dispose();
      this.geometry = null;
      this.loaded = false;

      this.dispatchEvent({ type: 'dispose' });

      for (let i = 0; i < this.oneTimeDisposeHandlers.length; i++) {
        let handler = this.oneTimeDisposeHandlers[i];
        handler();
      }
      this.oneTimeDisposeHandlers = [];
    }
  }
}
